import { Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { MessageHandlerService } from 'app/common/common/message-handler/message-handler.service';
import { Helpers } from '@mi-tool/utils/helpers';
import { capitalize, isEqual } from 'lodash';
import { debounceTime, filter, finalize, tap } from 'rxjs/operators';
import { Sort } from '@angular/material/sort';
import { ESort } from '@mi-tool/enums';
import { PageEvent } from '@angular/material/paginator';
import { DateRange } from 'app/common/types';
import { DateUtility } from 'app/common/date-utility';
import { BNewInteraction } from 'app/modules/data-model/new-interaction/new-interaction';
import {
  NewInteractionService,
  TDraftInteractionParams,
} from '../data-model/new-interaction/new-interaction.service';
import {
  Config,
  newInquirerIdConfig,
  newNameStrIdConfig,
  newUserConfig,
} from 'app/common/common/search-select/search-select.config';
import {
  Nomenclatures,
  NomenclaturesService,
  Scopes,
} from 'app/modules/data-model/nomenclature/nomenclatures.service';
import { InteractionsService } from 'app/modules/data-model/interaction/interactions.service';
import { Router } from '@angular/router';
import { GenericDialogService } from 'app/common/common/generic-dialog/generic-dialog.service';
import { MetadataFilter } from 'app/common/util/metadata-filter';
import { BUser } from 'app/modules/data-model/user/user';
import { HttpErrorResponse } from '@angular/common/http';
import { base64ToPK } from 'app/modules/data-model/data-model';
import { AuthService } from 'app/auth/auth.service';
import {
  PERM_VIEW_ENQUIRER,
  PERM_VIEW_ENQUIRY_ASSIGNEE,
  PERM_VIEW_ENQUIRY_INSERTED,
} from 'app/common/common/constants/permissions';

const ALL_COLUMNS = [
  'view-details',
  'id',
  'insertedBy',
  'assignedTo',
  'inquirer',
  'team',
  'topic',
  'category',
  'product',
  'language',
  'event',
  'createdTs',
  'dueDate',
  'priority',
  'isPublic',
  'title',
  'delete',
];

@Component({
  selector: 'app-my-drafts',
  templateUrl: './my-drafts.component.html',
  styleUrls: ['./my-drafts.component.scss'],
})
export class MyDraftsComponent implements OnInit, OnDestroy {
  draftInteractionsList: Array<BNewInteraction> = [];
  loading = true;
  displayedColumns: string[] = ALL_COLUMNS;

  appliedFilters$ = new BehaviorSubject<TDraftInteractionParams>(null);
  lastAppliedFilters: TDraftInteractionParams = null;
  priorities: string[] = ['Normal', 'High', 'Critical'];
  isPublicChoices: boolean[] = [true, false];
  hoveredInteractionPk: number = null;

  searchedId: number = null;
  searchedInquirerIds: string[] = null;
  searchedTeamIds: string[] = null;
  searchedTopicIds: string[] = null;
  searchedCategoryIds: string[] = null;
  searchedProductIds: string[] = null;
  searchedInsertedByIds: string[] = null;
  searchedAssignedToIds: string[] = null;
  searchedLanguageIds: string[] = null;
  searchedEventIds: string[] = null;
  searchedCreatedTs: DateRange = null;
  searchedDueDate: DateRange = null;
  searchedPriority: string = null;
  searchedIsPublic: string = 'all';

  teamConfig: Config = newNameStrIdConfig([]);
  eventConfig: Config = newNameStrIdConfig([]);
  topicConfig: Config = newNameStrIdConfig([]);
  productConfig: Config = newNameStrIdConfig([]);
  languageConfig: Config = newNameStrIdConfig([]);
  categoryConfig: Config = newNameStrIdConfig([]);
  inquirerConfig: Config = newInquirerIdConfig([]);
  insertedByConfig: Config = newUserConfig([]);
  assignedToConfig: Config = newUserConfig([]);
  inlineEditEventConfig: Config = newNameStrIdConfig([]);

  metadata = new MetadataFilter(true);
  selectedIsPublic: boolean = false;
  selectedPriority: string = null;
  selectedEventId: string = null;
  selectedTeamId: number = null;
  selectedUser: BUser;

  orderBy: string = null;
  pageSizeOptions = [5, 10, 25, 50, 100];
  pageSize = this.pageSizeOptions[1];
  pageIndex = 0;
  totalRecords = 0;
  private subs = new Subscription();

  constructor(
    private newInteractionService: NewInteractionService,
    private interactionsService: InteractionsService,
    private messageService: MessageHandlerService,
    private nomenclatures: NomenclaturesService,
    private dialogService: GenericDialogService,
    private authService: AuthService,
    private helpers: Helpers,
    private router: Router
  ) {}

  ngOnInit(): void {
    const scopes = new Scopes()
      .users()
      .teams()
      .events()
      .topics()
      .products()
      .languages()
      .categories()
      .draftNewInquirers();
    this.nomenclatures.get(scopes).subscribe((resp: Nomenclatures) => {
      this.teamConfig = newNameStrIdConfig(resp.teams, '');
      this.eventConfig = newNameStrIdConfig(resp.events, '');
      this.topicConfig = newNameStrIdConfig(resp.topics, '');
      this.productConfig = newNameStrIdConfig(resp.products, '');
      this.categoryConfig = newNameStrIdConfig(resp.categories, '');
      this.languageConfig = newNameStrIdConfig(resp.languages, '');
      this.inquirerConfig = newInquirerIdConfig(resp.draftNewInquirers, '', '', false);
      this.insertedByConfig = newUserConfig(resp.users, '');
      this.assignedToConfig = newUserConfig(resp.users, '');
      this.inlineEditEventConfig = newNameStrIdConfig(resp.events, 'Event');

      this.metadata.allTopics = resp.topics;
      this.metadata.allCategories = resp.categories;
      this.metadata.allProducts = resp.products;
    });

    this.subs.add(
      this.appliedFilters$
        .pipe(
          debounceTime(300),
          filter((appliedFilters) => !isEqual(appliedFilters, this.lastAppliedFilters)),
          tap(() => (this.loading = true))
        )
        .subscribe((appliedFilters) => {
          this.fetchDraftInteractionsList();
          this.lastAppliedFilters = appliedFilters;
        })
    );
    this.subs.add(
      this.authService.user.subscribe((user) => {
        if (user) {
          this.metadata.allProducts = user.resolveAllowedProducts();
        }
      })
    );
    this.subs.add(
      this.authService.permissions.subscribe((handler) => {
        const colName2Permission = {
          inquirer: PERM_VIEW_ENQUIRER,
          assignedTo: PERM_VIEW_ENQUIRY_ASSIGNEE,
          insertedBy: PERM_VIEW_ENQUIRY_INSERTED,
        };
        this.displayedColumns = ALL_COLUMNS.filter((colName) => {
          const permissionName = colName2Permission[colName];
          return !permissionName || handler.has(permissionName);
        });
      })
    );
    this.triggerSearch();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onSortChange(event: Sort): void {
    this.orderBy = event.direction
      ? (event.direction == 'desc' ? ESort.DESC : ESort.ASC) + event.active
      : null;
    this.triggerSearch();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.triggerSearch();
  }

  triggerSearch(): void {
    this.appliedFilters$.next({ ...this.getFilters() });
  }

  interactionDetailUrl(interaction: BNewInteraction): void {
    this.router.navigate([`/new/${interaction.pk().toString()}`]);
  }

  deleteDraftInteraction(interaction: BNewInteraction) {
    const message = `${this.helpers.translate(
      'YOU_ARE_ABOUT_TO_DELETE_THE_DRAFT_INTERACTION'
    )} <br><b>${this.helpers.translate('THE_ACTION_IS_IRREVERSIBLE')}</b>`;

    this.subs.add(
      this.dialogService.confirmDeleteAction(message).subscribe(() => {
        this.interactionsService.deleteDraft(interaction.pk()).subscribe({
          next: () => {
            this.fetchDraftInteractionsList();
            this.messageService.info('The draft interaction has been successfully deleted!');
          },
          error: () =>
            this.messageService.error('An error occurred while deleting the draft interaction.'),
        });
      })
    );
  }

  setPriority(event: string, interaction: BNewInteraction): void {
    this.subs.add(
      this.interactionsService.setPriority(interaction.pk(), true, event.toLowerCase()).subscribe({
        next: () => this.fetchDraftInteractionsList(),
        error: (err) => this.messageService.httpError('Update draft Priority failed', err),
      })
    );
  }

  setPublic(event: boolean, interaction: BNewInteraction): void {
    this.subs.add(
      this.interactionsService.setPublic(interaction.pk(), event).subscribe({
        next: () => this.fetchDraftInteractionsList(),
        error: (err) => this.messageService.httpError('Update draft Public failed', err),
      })
    );
  }

  setEvent(event: string | undefined, interaction: BNewInteraction): void {
    const eventId = event ? base64ToPK(event) : undefined;
    this.subs.add(
      this.interactionsService.setEvent(interaction.pk(), true, eventId).subscribe({
        next: () => this.fetchDraftInteractionsList(),
        error: (err) => this.messageService.httpError('Update draft Event failed', err),
      })
    );
  }

  setTopicRelated(interaction: BNewInteraction): void {
    const initialProductList = interaction.productList;
    const initialCategoryList = interaction.categoryList;
    const initialTopicList = interaction.topicList;
    interaction.productList = this.metadata?.selectedProduct?.name;
    interaction.categoryList = this.metadata?.selectedCategory?.name;
    interaction.topicList = this.metadata?.selectedTopic?.name;
    this.subs.add(
      this.interactionsService
        .setTopic({
          ids: [interaction.pk()],
          isDraft: true,
          productId: this.metadata?.selectedProduct?.pk(),
          categoryId: this.metadata?.selectedCategory?.pk(),
          topicId: this.metadata?.selectedTopic?.pk(),
        })
        .subscribe({
          next: (response) => {
            if (response['message']) {
              interaction.isInlineEdit = false;
              interaction.productList = initialProductList;
              interaction.categoryList = initialCategoryList;
              interaction.topicList = initialTopicList;
            }
          },
          error: (error: HttpErrorResponse) =>
            this.messageService.error(
              `(Update draft Topic Related Data failed): ${error.statusText}`
            ),
        })
    );
  }

  onReassign(): void {
    this.fetchDraftInteractionsList();
  }

  activateInlineEdit(index: number): void {
    this.draftInteractionsList.forEach((interaction, i: number) => {
      if (i === index && !interaction.isInlineEdit) {
        interaction.isInlineEdit = true;
        this.selectedUser = interaction?.assignedTo;
        this.selectedTeamId = interaction?.team.pk();
        this.selectedEventId = interaction?.event.id;
        this.selectedIsPublic = interaction?.isPublic;
        this.selectedPriority = interaction?.priority ? capitalize(interaction?.priority) : null;

        // Products
        const productIds = interaction.inquiries
          .filter((i) => i.product)
          .map((i) => i.product.id)
          .filter((val, idx, arr) => arr.indexOf(val) === idx);
        const selectedProduct =
          productIds.length === 1 && this.metadata.availableProducts
            ? this.metadata.availableProducts.find((p) => p.id === productIds[0])
            : undefined;

        // Categories
        const categoryIds = interaction.inquiries
          .filter((i) => i.category)
          .map((i) => i.category.id)
          .filter((val, idx, arr) => arr.indexOf(val) === idx);
        const selectedCategory =
          categoryIds.length === 1 && this.metadata.availableCategories
            ? this.metadata.availableCategories.find((c) => c.id === categoryIds[0])
            : undefined;

        // Topics
        const topicIds = interaction.inquiries
          .filter((i) => i.topic)
          .map((i) => i.topic.id)
          .filter((val, idx, arr) => arr.indexOf(val) === idx);
        const selectedTopic =
          topicIds.length === 1 && this.metadata.availableTopics
            ? this.metadata.availableTopics.find((t) => t.id === topicIds[0])
            : undefined;

        this.metadata.refresh(selectedProduct, selectedCategory, selectedTopic);
      } else {
        interaction.isInlineEdit = false;
      }
    });
  }

  private getFilters(): TDraftInteractionParams {
    return {
      pageIndex: this.pageIndex + 1,
      pageSize: this.pageSize,
      orderBy: this.orderBy,
      id: this.searchedId,
      inquirerIds: this.helpers.arrayToStringOrNull(this.searchedInquirerIds),
      teamIds: this.helpers.arrayToStringOrNull(this.searchedTeamIds),
      topicIds: this.helpers.arrayToStringOrNull(this.searchedTopicIds),
      categoryIds: this.helpers.arrayToStringOrNull(this.searchedCategoryIds),
      productIds: this.helpers.arrayToStringOrNull(this.searchedProductIds),
      insertedByIds: this.helpers.arrayToStringOrNull(this.searchedInsertedByIds),
      assignedToIds: this.helpers.arrayToStringOrNull(this.searchedAssignedToIds),
      languageIds: this.helpers.arrayToStringOrNull(this.searchedLanguageIds),
      eventIds: this.helpers.arrayToStringOrNull(this.searchedEventIds),
      createdTs: DateUtility.formatDateRange(this.searchedCreatedTs),
      dueDate: DateUtility.formatDateRange(this.searchedDueDate),
      priority: this.searchedPriority,
      isPublic: this.searchedIsPublic != 'all' ? this.searchedIsPublic : null,
    };
  }

  private fetchDraftInteractionsList(): void {
    this.newInteractionService
      .fetchDraftInteractionsList(this.appliedFilters$.getValue())
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          this.draftInteractionsList = response.records;
          this.totalRecords = response.totalRecords;
        },
        error: () =>
          this.messageService.error('An error occurred while fetching the draft interactions.'),
      });
  }
}
